import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CONTACT_EMAIL } from "@/hooks/useLeadSubmit";
import { gtmEnabled, openConsentSettings } from "@/lib/consent";

const LAST_UPDATED = "20 October 2026";

const link =
  "font-medium text-primary underline underline-offset-4 rounded-sm focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring";

const COOKIES: { name: string; set: string; purpose: string; lasts: string }[] = [
  {
    name: "ab_attrib (cookie)",
    set: "When you arrive from an ad or a tagged campaign link",
    purpose: "Remembers which ad or campaign brought you, so an enquiry you send can be credited to it.",
    lasts: "90 days",
  },
  {
    name: "ab-consent (browser storage)",
    set: "When you accept or reject cookies",
    purpose: "Remembers your cookie choice.",
    lasts: "Until you clear it",
  },
  {
    name: "ab-a11y-prefs (browser storage)",
    set: "When you change a display setting",
    purpose: "Remembers accessibility settings such as text size and theme.",
    lasts: "Until you clear it",
  },
  {
    name: "Google Analytics and Google Ads cookies (e.g. _ga, _gcl_au)",
    set: "Only if you accept cookies",
    purpose: "Measure visits and which ads lead to enquiries.",
    lasts: "Set by Google; see Google's policy",
  },
];

/**
 * Privacy policy. Linked from every lead form, the cookie banner and the
 * footer. Content reflects what the site actually does (see lib/attribution,
 * lib/consent, lib/analytics, hooks/useLeadSubmit).
 */
const Privacy = () => (
  <div className="min-h-screen bg-background">
    <Seo
      title="Privacy Policy"
      description="How AccessBharat collects, uses and protects the personal data you share through accessbharat.com, and the choices you have."
      path="/privacy"
    />
    <Navbar />
    <main id="main-content" tabIndex={-1} className="section-padding pt-28 md:pt-[9rem]">
      <article className="mx-auto max-w-3xl text-base leading-relaxed text-muted-foreground [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-foreground [&_li]:mb-2 [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6">
        <h1 className="mb-3 font-display text-4xl font-bold text-foreground md:text-5xl">Privacy policy</h1>
        <p>Last updated: {LAST_UPDATED}</p>

        <p>
          This policy explains what personal data AccessBharat collects through accessbharat.com, why, and the
          choices you have. We collect as little as we need to answer your enquiry and run our business.
        </p>

        <h2>Who we are</h2>
        <p>
          AccessBharat provides web accessibility audits, testing and remediation from Mumbai, Maharashtra, India.
          For anything about your data, email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className={link}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>

        <h2>What we collect</h2>
        <ul>
          <li>
            <strong className="text-foreground">What you type into our forms:</strong> your name, email, and
            anything else you choose to give — phone, company, the service and timeline you need, the sites or
            documents to test, a message, and a preferred demo time.
          </li>
          <li>
            <strong className="text-foreground">How you found us:</strong> if you arrive from an ad or a tagged
            link, the ad click identifier (such as Google's gclid), the campaign tags in the link (utm_source,
            utm_campaign and similar), the page you landed on and when. These are sent with an enquiry only if you
            submit one.
          </li>
          <li>
            <strong className="text-foreground">Site usage:</strong> pages visited and how the site is used,
            measured with our self-hosted Umami analytics, which uses no cookies and does not identify you. It may
            also record anonymised sessions to help us find usability and accessibility problems.
          </li>
          <li>
            <strong className="text-foreground">Google Analytics and Google Ads</strong>, only if you accept
            cookies — see below.
          </li>
        </ul>

        <h2>How we use it</h2>
        <ul>
          <li>To reply to your enquiry, scope and price the work, and keep a record of our conversation.</li>
          <li>To understand which ads and pages bring enquiries, so we spend our marketing budget well.</li>
          <li>To keep the site secure and stop spam.</li>
        </ul>
        <p>We do not sell your data, and we do not use it for anything unrelated to these purposes.</p>

        <h2>Google Ads measurement</h2>
        <p>
          If you came from a Google ad and send us an enquiry, we may later tell Google Ads that the click led to
          an enquiry or a project, so Google can measure our ads. To match the enquiry to the click we send the
          click identifier and, if you accepted cookies, your email address and phone number, which Google
          receives in hashed (scrambled) form. Google's use of this data is governed by{" "}
          <a href="https://policies.google.com/privacy" className={link}>
            Google's privacy policy
          </a>
          .
        </p>

        <h2>Cookies and similar storage</h2>
        <p>
          Google cookies are off until you choose &ldquo;Accept&rdquo; in the cookie banner; if you choose
          &ldquo;Reject&rdquo;, Google tags run without cookies.
          {gtmEnabled() && (
            <>
              {" "}
              You can change your choice at any time:{" "}
              <button type="button" onClick={openConsentSettings} className={link}>
                Cookie settings
              </button>
              .
            </>
          )}
        </p>
        {/* Scrolls sideways on narrow screens; focusable so keyboard users can
            scroll it too (WCAG 2.1.1). */}
        <div
          className="mb-4 overflow-x-auto rounded-sm focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
          role="region"
          aria-label="Cookies table"
          tabIndex={0}
        >
          <table className="w-full min-w-[36rem] border-collapse text-left text-sm">
            <caption className="sr-only">Cookies and browser storage used by this site</caption>
            <thead>
              <tr className="border-b text-foreground">
                <th scope="col" className="py-2 pr-4 font-semibold">Name</th>
                <th scope="col" className="py-2 pr-4 font-semibold">When it is set</th>
                <th scope="col" className="py-2 pr-4 font-semibold">Purpose</th>
                <th scope="col" className="py-2 font-semibold">How long</th>
              </tr>
            </thead>
            <tbody>
              {COOKIES.map((c) => (
                <tr key={c.name} className="border-b align-top">
                  <th scope="row" className="py-2 pr-4 font-medium text-foreground">{c.name}</th>
                  <td className="py-2 pr-4">{c.set}</td>
                  <td className="py-2 pr-4">{c.purpose}</td>
                  <td className="py-2">{c.lasts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2>Who we share it with</h2>
        <p>
          Our own team, and service providers who process data for us: our hosting provider (Amazon Web Services,
          servers in India), our email provider, and Google when you have come from or interacted with our ads as
          described above. Each may process data only on our instructions. We disclose data to authorities only
          where the law requires it.
        </p>

        <h2>How long we keep it</h2>
        <p>
          We keep enquiry details while we are in touch with you and for as long as we need them for our business
          records or the law requires. You can ask us to delete them at any time.
        </p>

        <h2>Your rights</h2>
        <p>
          Under India's Digital Personal Data Protection Act, 2023 you can ask us for a summary of the personal data
          we hold about you, to correct or complete it, to erase it, and to withdraw any consent you gave. You can
          also nominate someone to exercise these rights for you. Email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className={link}>
            {CONTACT_EMAIL}
          </a>{" "}
          and we will respond as soon as we can. If you are not satisfied with our response, you may complain to the
          Data Protection Board of India.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          We will update this page if what we collect or how we use it changes, and change the date at the top.
          Questions? <Link to="/#contact" className={link}>Contact us</Link>.
        </p>
      </article>
    </main>
    <Footer />
  </div>
);

export default Privacy;
